import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import { guestApi } from "../utils/api";
import {
  MapPin, Clock, Truck, Car, Bus, Package, Bike,
  RefreshCw, AlertTriangle, ArrowUpRight, ArrowDownLeft, Navigation,
} from "lucide-react";

// Fix Leaflet default icon path in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl:       require("leaflet/dist/images/marker-icon.png"),
  shadowUrl:     require("leaflet/dist/images/marker-shadow.png"),
});

const INDIA_CENTER = [22.5, 80.0];
const STATUS_COLOR = { Active: "#16A34A", Idle: "#D97706", Offline: "#9CA3AF" };
const TYPE_ICONS   = { Car, Truck, Bus, "Two-Wheeler": Bike, Other: Package };

const STATUS_BADGE = {
  Active:  { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0", dot: "#16A34A",  label: "Active" },
  Idle:    { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A", dot: "#F59E0B",  label: "Idle"   },
  Offline: { bg: "#F9FAFB", color: "#6B7280", border: "#E5E7EB", dot: "#9CA3AF",  label: "Offline" },
};

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function timeAgo(iso) {
  if (!iso) return "–";
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}
function fmtExpiry(iso) {
  if (!iso) return "Never expires";
  const d = new Date(iso);
  if (d < new Date()) return "Expired";
  return `Expires ${fmtDate(iso)} ${fmtTime(iso)}`;
}

// Build a vehicle marker icon (same visual language as MapView.jsx)
function makeGuestIcon(vehicle) {
  const color = STATUS_COLOR[vehicle.status] || "#9CA3AF";
  const truckSvg = `<svg width="20" height="14" viewBox="0 0 32 20" fill="none">
    <rect x="0" y="4" width="20" height="12" rx="2" fill="#fff"/>
    <path d="M20 7h7l3 5v4h-10V7z" fill="#fff"/>
    <circle cx="6" cy="17" r="3" fill="#fff" stroke="${color}" stroke-width="1.5"/>
    <circle cx="24" cy="17" r="3" fill="#fff" stroke="${color}" stroke-width="1.5"/>
  </svg>`;
  return L.divIcon({
    className: "",
    iconSize:  [72, 76],
    iconAnchor:[36, 76],
    html: `
      <div style="width:72px;height:76px;display:flex;flex-direction:column;align-items:center;">
        <div style="
          width:44px;height:44px;border-radius:50% 50% 50% 4px;
          transform:rotate(-45deg);
          background:${color};
          box-shadow:0 4px 18px ${color}66,0 2px 8px rgba(0,0,0,0.22);
          display:flex;align-items:center;justify-content:center;
          flex-shrink:0;border:2.5px solid #ffffff;
        ">
          <div style="transform:rotate(45deg);display:flex;align-items:center;justify-content:center;">
            ${truckSvg}
          </div>
        </div>
        <div style="
          margin-top:4px;padding:1px 5px;
          background:#1E293B;border-radius:4px;
          font-size:9px;font-weight:700;color:#fff;
          font-family:'SF Mono','Fira Code',monospace;
          letter-spacing:0.05em;white-space:nowrap;
        ">${(vehicle.plateNumber || "").slice(-6)}</div>
      </div>`,
  });
}

// Auto-pan the map to the vehicle's location
function MapPanner({ lat, lng }) {
  const map = useMap();
  const didPan = useRef(false);
  useEffect(() => {
    if (lat && lng && !didPan.current) {
      map.setView([lat, lng], 11, { animate: true });
      didPan.current = true;
    }
  }, [lat, lng, map]);
  return null;
}

function laneInfo(dir) {
  const map = {
    E: { label: "Entry", Icon: ArrowDownLeft, color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
    X: { label: "Exit",  Icon: ArrowUpRight,  color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
  };
  return map[dir?.toUpperCase()] || { label: "", Icon: Navigation, color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB" };
}

// ── Error states ──────────────────────────────────────────────────────────────
function ErrorScreen({ type, message }) {
  const configs = {
    invalid:  { icon: "🔗", title: "Link Unavailable",  color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
    expired:  { icon: "⏰", title: "Link Expired",       color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
    offline:  { icon: "📡", title: "Connection Error",   color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB" },
    notfound: { icon: "🚫", title: "Link Not Found",     color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
  };
  const cfg = configs[type] || configs.invalid;
  return (
    <div style={{
      minHeight: "100vh", background: "#F8FAFC",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{cfg.icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{cfg.title}</div>
      <div style={{
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        borderRadius: 10, padding: "12px 20px", marginBottom: 24,
        fontSize: 14, color: cfg.color, maxWidth: 360, textAlign: "center", lineHeight: 1.6,
      }}>
        {message}
      </div>
      <a href="/" style={{
        background: "#0F172A", color: "#fff", textDecoration: "none",
        borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 600,
      }}>
        Open LorryKhojo
      </a>
    </div>
  );
}

// ── Main guest view ───────────────────────────────────────────────────────────
export default function GuestView() {
  const { token } = useParams();
  const [state, setState]     = useState("loading"); // loading | ok | invalid | expired | offline
  const [data, setData]       = useState(null);      // { vehicle, share }
  const [lastFetch, setLastFetch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  async function fetchData(isManual = false) {
    if (isManual) setRefreshing(true);
    try {
      const res = await guestApi.get(`/api/guest/${token}`);
      setData(res.data);
      setState("ok");
      setLastFetch(Date.now());
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setState("invalid");
      else if (status === 410) setState("expired");
      else if (state !== "ok") setState("offline");
      // If we already have data and network fails, keep showing stale data
    } finally {
      if (isManual) setRefreshing(false);
    }
  }

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(() => fetchData(), 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [token]); // eslint-disable-line

  if (state === "loading") {
    return (
      <div style={{
        minHeight: "100vh", background: "#F8FAFC",
        display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12,
      }}>
        <div style={{ width: 32, height: 32, border: "3px solid #E5E7EB", borderTopColor: "#4F46E5", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        <div style={{ fontSize: 14, color: "#9CA3AF" }}>Loading vehicle data…</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (state === "invalid")  return <ErrorScreen type="invalid"  message="This share link is invalid or has been revoked by the vehicle owner." />;
  if (state === "expired")  return <ErrorScreen type="expired"  message="This share link has expired. Ask the vehicle owner to generate a new link." />;
  if (state === "offline" && !data) return <ErrorScreen type="offline" message="Could not reach the tracking server. Please check your internet connection and try again." />;

  const { vehicle, share } = data;
  const VehicleIcon = TYPE_ICONS[vehicle.type] || Package;
  const badge = STATUS_BADGE[vehicle.status] || STATUS_BADGE.Offline;
  const hasLocation = vehicle.lat && vehicle.lng;
  const tollCount = vehicle.tollHistory?.length || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#F8FAFC", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        * { box-sizing: border-box; }
      `}</style>

      {/* ── Navbar ── */}
      <nav style={{
        background: "#0F172A", padding: "0 20px",
        height: 52, display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Truck size={14} color="#fff" />
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#F1F5F9", letterSpacing: -0.3 }}>LorryKhojo</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            background: "rgba(79,70,229,0.2)", border: "1px solid rgba(99,102,241,0.4)",
            borderRadius: 20, padding: "3px 10px",
            fontSize: 11, fontWeight: 600, color: "#A5B4FC", letterSpacing: 0.3,
          }}>SHARED VIEW</div>
          <div style={{ fontSize: 11, color: "#475569" }}>{fmtExpiry(share.expiresAt)}</div>
        </div>
      </nav>

      {/* ── Vehicle header ── */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #E5E7EB",
        padding: "14px 20px", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <VehicleIcon size={18} color="#6B7280" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{vehicle.nickname}</span>
              <span style={{
                fontFamily: "'SF Mono','Fira Code',monospace",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                color: "#374151", background: "#F7F8F9",
                border: "1px solid #EBEBED", borderRadius: 5,
                padding: "2px 8px",
              }}>{vehicle.plateNumber}</span>
              <div style={{
                display: "flex", alignItems: "center", gap: 5,
                background: badge.bg, border: `1px solid ${badge.border}`,
                borderRadius: 20, padding: "3px 10px",
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: "50%", background: badge.dot,
                  animation: vehicle.status === "Active" ? "livePulse 1.5s ease-in-out infinite" : "none",
                }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: badge.color }}>{badge.label}</span>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>
              {vehicle.type}{vehicle.ownerName ? ` · ${vehicle.ownerName}` : ""}
            </div>
          </div>

          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
              padding: "7px 14px", fontSize: 12, fontWeight: 500, color: "#6B7280",
              cursor: refreshing ? "not-allowed" : "pointer", opacity: refreshing ? 0.6 : 1,
            }}
          >
            <RefreshCw size={12} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>

        {/* Map */}
        <div style={{ height: "52vh", position: "relative", flexShrink: 0 }}>
          {hasLocation ? (
            <MapContainer
              center={INDIA_CENTER}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              zoomControl={true}
              scrollWheelZoom={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              <MapPanner lat={vehicle.lat} lng={vehicle.lng} />

              {/* Route polyline */}
              {vehicle.route && vehicle.route.length > 1 && (
                <Polyline
                  positions={vehicle.route.map(p => [p.lat, p.lng])}
                  color="#4F46E5"
                  weight={3}
                  opacity={0.6}
                  dashArray="6 4"
                />
              )}

              {/* Toll plaza circles */}
              {vehicle.tollHistory?.slice(0, 15).map((toll, i) => (
                toll.lat && toll.lng ? (
                  <CircleMarker
                    key={i}
                    center={[toll.lat, toll.lng]}
                    radius={5}
                    color="#4F46E5"
                    fillColor="#818CF8"
                    fillOpacity={0.7}
                    weight={1.5}
                  />
                ) : null
              ))}

              {/* Vehicle marker */}
              <Marker
                position={[vehicle.lat, vehicle.lng]}
                icon={makeGuestIcon(vehicle)}
              />
            </MapContainer>
          ) : (
            <div style={{
              height: "100%", background: "#F1F5F9",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 12,
            }}>
              <MapPin size={32} color="#CBD5E1" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#64748B" }}>Location not yet available</div>
                <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>
                  The vehicle owner needs to track this vehicle first.
                </div>
              </div>
            </div>
          )}

          {/* Last refreshed indicator */}
          {lastFetch && (
            <div style={{
              position: "absolute", bottom: 10, right: 10, zIndex: 1000,
              background: "rgba(15,23,42,0.8)", backdropFilter: "blur(4px)",
              borderRadius: 8, padding: "5px 10px",
              fontSize: 11, color: "#94A3B8",
            }}>
              Updated {timeAgo(new Date(lastFetch).toISOString())}
            </div>
          )}
        </div>

        {/* ── Info section ── */}
        <div style={{ flex: 1, padding: "16px 20px 24px", display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, width: "100%", margin: "0 auto" }}>

          {/* No data banner */}
          {vehicle.noRecentData && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#FFFBEB", border: "1px solid #FDE68A",
              borderRadius: 10, padding: "10px 14px",
            }}>
              <AlertTriangle size={16} color="#D97706" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>No recent FASTag crossings</div>
                <div style={{ fontSize: 12, color: "#B45309", marginTop: 1 }}>
                  {vehicle.isStateLocation
                    ? "Showing estimated location based on registration district."
                    : "No toll crossings recorded in the last 30 days."}
                </div>
              </div>
            </div>
          )}

          {/* Location + last updated cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{
              background: "#fff", border: "1px solid #F0F1F3", borderRadius: 10, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                <MapPin size={10} color="#9CA3AF" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>Location</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.4 }}>
                {vehicle.location || "Not available"}
              </div>
            </div>
            <div style={{
              background: "#fff", border: "1px solid #F0F1F3", borderRadius: 10, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                <Clock size={10} color="#9CA3AF" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>Last Updated</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                {vehicle.lastUpdated ? timeAgo(vehicle.lastUpdated) : "–"}
              </div>
              {vehicle.lastUpdated && (
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>
                  {fmtDate(vehicle.lastUpdated)} · {fmtTime(vehicle.lastUpdated)}
                </div>
              )}
            </div>
          </div>

          {/* Route history */}
          {tollCount > 0 && (
            <div style={{ background: "#fff", border: "1px solid #F0F1F3", borderRadius: 10, overflow: "hidden" }}>
              <div style={{
                padding: "12px 16px", borderBottom: "1px solid #F5F5F5",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Recent Toll Crossings</span>
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>{tollCount} crossing{tollCount !== 1 ? "s" : ""}</span>
              </div>
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {vehicle.tollHistory.slice(0, 10).map((toll, i) => {
                  const lane = laneInfo(toll.direction);
                  const LaneIcon = lane.Icon;
                  return (
                    <div key={i} style={{
                      padding: "12px 16px",
                      borderBottom: i < Math.min(tollCount, 10) - 1 ? "1px solid #F5F5F5" : "none",
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                      {lane.label && (
                        <div style={{
                          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                          background: lane.bg, border: `1px solid ${lane.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <LaneIcon size={12} color={lane.color} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {toll.plaza || toll.tollName || "Toll Plaza"}
                        </div>
                        {toll.location && (
                          <div style={{ fontSize: 12, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {toll.location}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>
                          {toll.timestamp ? fmtTime(toll.timestamp) : "–"}
                        </div>
                        <div style={{ fontSize: 11, color: "#B0B7C3" }}>
                          {toll.timestamp ? fmtDate(toll.timestamp) : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        background: "#fff", borderTop: "1px solid #E5E7EB",
        padding: "12px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ fontSize: 12, color: "#9CA3AF" }}>
          This is a read-only shared view. You cannot modify any data.
        </div>
        <a
          href="/"
          style={{
            fontSize: 12, fontWeight: 600, color: "#4F46E5",
            textDecoration: "none", display: "flex", alignItems: "center", gap: 4,
          }}
        >
          <Truck size={11} /> Track your own fleet on LorryKhojo →
        </a>
      </div>
    </div>
  );
}
